import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import SubscriptionApiService from '../../api/SubscriptionApiService';
import {Box, CircularProgress} from '@mui/material';
import PropTypes from 'prop-types';
import PageShell from '../PageShell';
import StandardUserEditButton from './OptionsButton/StandardUserEditButton';
import ConfirmDeleteModal from '../Modal/StandardFunctions/ConfirmDeleteModal';
import StorageUnitCardMobile from '../Mobile/StorageUnitCardMobile';
import ChangePaymentModal from '../Modal/StandardFunctions/ChangePaymentModal';
import PayBalanceModal from '../Modal/StandardFunctions/PayBalanceModal';
import AccountBalancePanel from './AccountBalancePanel';
import AccountApiService from '../../api/AccountApiService';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import Backdrop from '@mui/material/Backdrop';
import * as Constants from '../../utilities/Constants';
import {useNavigate} from 'react-router-dom';
import {Label} from 'reactstrap';

export default function UsersStorageUnits(props) {
    const { setSnackbarProperties, isMobile } = props;
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCancelSubscription, setShowCancelSubscription] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState({});
    const [showEditPayment, setShowEditPayment] = useState(false);
    const [statement, setStatement] = useState(null);
    const [statementLoading, setStatementLoading] = useState(false);
    const [showPayBalance, setShowPayBalance] = useState(false);

    const subscriptionApiService = new SubscriptionApiService();
    const accountApiService = new AccountApiService();

    const navigate = useNavigate();

    const columns = [
        {
            field: 'storageUnit.name',
            headerName: 'Storage Unit',
            flex: 1,
            renderCell: (params) => {
                return params.row.storageUnit.name;
            }
        },
        {
            field: 'storageSize',
            headerName: 'Storage Size',
            flex: 1,
            renderCell: (params) => {
                return params.row.storageUnit.storageSize;
            }
        },
        {
            field: 'storageUnitType',
            headerName: 'Storage Type',
            flex: 1,
            renderCell: (params) => {
                return params.row.storageUnit.storageUnitType.name;
            }
        },
        {
            field: 'climateControlled',
            headerName: 'Climate Controlled',
            flex: 1,
            renderCell: (params) => {
                return params.row.storageUnit.climateControlled ? 'Yes' : 'No';
            }
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            renderCell: (params) => {
                return Constants.USDollar.format(params.row.storageUnit.price);
            }
        },
        {
            field: 'nextReadyDate',
            headerName: 'End Date',
            flex: 1,
            renderCell: (params) => {
                const readyDate = params.row.storageUnit.nextReadyDate;
                return readyDate ? readyDate.toString().substring(0, readyDate.toString().length - 9) : 'N/A';
            }
        },
        {
            field: 'options',
            headerName: 'Options',
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <StandardUserEditButton
                    params={params}
                    setShowDeleteDialog={setShowCancelSubscription}
                    setCurrentSelectedObject={setSelectedSubscription}
                    setShowEditDialog={setShowEditPayment}
                    editButtonText={'Edit Payment Method'}
                    deleteButtonText={params.row.storageUnit.nextReadyDate ? 'Already Cancelled' : 'Cancel Subscription'}
                    showEditButton={true}
                    showDeleteButton={true}
                    disableDeleteButton={params.row.storageUnit.nextReadyDate}
                />;
            },
        },
    ];

    async function handleConfirmCancelSubscription() {
        const response = await subscriptionApiService.cancelSubscription(selectedSubscription);

        if(response.success) {
            setSnackbarProperties('Cancelled Subscription');
            await getUserStorageUnits();
        } else {
            setSnackbarProperties(`Error cancelling subscription: ${response.message}`, 'error');
        }

        closeCancelConfirm();
    }

    function closeCancelConfirm() {
        setShowCancelSubscription(false);
        setSelectedSubscription(undefined);
    }

    /**
     * Loads the tenant's balance.
     *
     * Failures are logged rather than shown. This sits above a page whose actual job is listing
     * units, and a snackbar saying the balance could not be loaded would be alarming out of
     * proportion to a panel that simply does not appear.
     */
    async function getAccountStatement() {
        setStatementLoading(true);

        const response = await accountApiService.getMyStatement();

        if (response.success) {
            setStatement(response.data);
        } else {
            console.error('Unable to load account balance', response.message);
        }

        setStatementLoading(false);
    }

    async function getUserStorageUnits() {
        setLoading(true);

        const storageUnitsResponse = await subscriptionApiService.getSubscriptionsForUser();

        if(storageUnitsResponse.success) {
            setSubscriptions(storageUnitsResponse.data);
        } else {
            setSnackbarProperties(`Unable to retrieve storage units: ${storageUnitsResponse.message}`, 'error');
        }
        setLoading(false);
    }

    function hideCancelModal() {
        setShowCancelSubscription(false);
    }

    function showEditPaymentModal(subscription) {
        setSelectedSubscription(subscription);
        setShowEditPayment(true);
    }

    function showCancelSubscriptionModal(subscription) {
        setSelectedSubscription(subscription);
        setShowCancelSubscription(true);
    }

    useEffect(() => {
        if(!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);
        }
        document.title = 'Executive Storage Suites - Your Storage Units';
        getUserStorageUnits();
        getAccountStatement();
    }, [showEditPayment]);

    return(
        <PageShell
            title={'Your Storage Units'}
            subtitle={loading ? 'Loading...' : `${subscriptions.length} unit${subscriptions.length === 1 ? '' : 's'}`}
            isMobile={isMobile}
        >
            <AccountBalancePanel
                statement={statement}
                loading={statementLoading}
                onPay={() => setShowPayBalance(true)}
                isMobile={isMobile}
            />

            { isMobile ? loading ? <Backdrop open={loading}><CircularProgress color={'inherit'} /></Backdrop> :
                subscriptions.length > 0 ? subscriptions.map((sub, index) => {
                    return <StorageUnitCardMobile
                        key={index}
                        storageUnit={sub.storageUnit}
                        isOwned={true}
                        setOpenEditPayment={() => showEditPaymentModal(sub)}
                        setOpenCancelUnit={() => showCancelSubscriptionModal(sub)}
                    />;
                }) : <Label>You have no subscriptions to storage units</Label>
                :
                <Box sx={{height: '80vh'}}>
                    <DataGrid
                        rows={subscriptions}
                        columns={columns}
                        loading={loading}
                        getRowHeight={() => 'auto'}
                        getRowId={(row) => row.id}
                    />
                </Box>

            }

            <ChangePaymentModal
                open={showEditPayment}
                close={() => setShowEditPayment(false)}
                subscription={selectedSubscription}
                userId={authenticationManager.getUserId()}
                isMobile={isMobile}
                setSnackbarProperties={setSnackbarProperties}
            />
            <PayBalanceModal
                open={showPayBalance}
                close={() => setShowPayBalance(false)}
                statement={statement}
                userId={authenticationManager.getUserId()}
                isMobile={isMobile}
                setSnackbarProperties={setSnackbarProperties}
                onPaid={getAccountStatement}
            />
            <ConfirmDeleteModal
                open={showCancelSubscription}
                title={'Cancel Storage Unit'}
                confirmText={'Are you sure you wish to cancel your subscription?'}
                handleConfirm={handleConfirmCancelSubscription}
                close={hideCancelModal}
            />
        </PageShell>
    );
}

UsersStorageUnits.propTypes = {
    setSnackbarProperties: PropTypes.func,
    isMobile: PropTypes.bool,
};
